/* eslint-disable formatjs/no-literal-string-in-jsx */

import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'

import { isEqual } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  ALL_SEARCHABLE_ITEM_PHASES,
  SmartSearchConfig,
} from '@lyrahealth-inc/shared-app-logic/src/features/smartSearch/constants'
import {
  FuzzySearchConfig,
  RRFConfig,
  SearchableItemPhase,
  SemanticSearchConfig,
  SmartSearchMode,
} from '@lyrahealth-inc/shared-app-logic/src/features/smartSearch/types'

import { BaseButton, ButtonSize, ButtonType } from '../../../atoms/baseButton/BaseButton'
import { BaseInputField } from '../../../atoms/baseInputField/BaseInputField'
import { BodyText } from '../../../atoms/bodyText/BodyText'
import { SecondaryButton } from '../../../atoms/secondaryButton/SecondaryButton'
import { ToggleSwitch } from '../../../molecules/toggleSwitch/ToggleSwitch'
import { BodyTextSize } from '../../../styles/typeStyles'
import { tID } from '../../../utils/utils'

const Container = styled.View({
  gap: 12,
})

const SectionHeader = styled(BodyText)({
  fontWeight: '600',
  marginTop: 16,
  marginBottom: 4,
})

const FieldRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
})

const FieldLabel = styled(BodyText)({
  flex: 1,
})

const FieldContent = styled.View({
  flex: 1,
})

const InputRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
})

const InputContainer = styled.View({
  width: 80,
})

const ButtonContainer = styled.View({
  marginTop: 16,
  flexDirection: 'row',
  gap: 12,
})

const PhaseToggleRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginVertical: 4,
})

const SectionDivider = styled.View(({ theme }) => ({
  marginVertical: 12,
  borderBottomWidth: 1,
  borderBottomColor: theme.colors.borderDefault,
}))

const ModeSelector = styled.View({
  flexDirection: 'row',
  gap: 8,
})

type ConfigFieldPath =
  | `fuzzy.${keyof FuzzySearchConfig}`
  | `semantic.${keyof SemanticSearchConfig}`
  | `rrf.${keyof RRFConfig}`
  | 'minQueryLength'
  | 'debounceMs'
  | 'scoreDiffThreshold'
  | 'maxResults'

interface ConfigField {
  path: ConfigFieldPath
  label: string
  step: number
  min: number
  max: number
  hint: string
}

const fuzzyFields: ConfigField[] = [
  {
    path: 'fuzzy.standardThreshold',
    label: 'Standard Threshold',
    step: 0.05,
    min: 0,
    max: 1,
    hint: '0-1, lower = stricter',
  },
  {
    path: 'fuzzy.strictThreshold',
    label: 'Strict Threshold',
    step: 0.05,
    min: 0,
    max: 1,
    hint: '0-1, lower = stricter',
  },
  {
    path: 'fuzzy.displayNameWeight',
    label: 'Display Name Weight',
    step: 1,
    min: 1,
    max: 10,
    hint: '1-10, higher = more important',
  },
  {
    path: 'fuzzy.keywordsWeight',
    label: 'Keywords Weight',
    step: 1,
    min: 1,
    max: 10,
    hint: '1-10, higher = more important',
  },
]

const semanticFields: ConfigField[] = [
  {
    path: 'semantic.similarityThreshold',
    label: 'Similarity Threshold',
    step: 0.05,
    min: 0,
    max: 1,
    hint: '0-1, higher = stricter',
  },
  {
    path: 'semantic.strictSimilarityThreshold',
    label: 'Strict Similarity Threshold',
    step: 0.05,
    min: 0,
    max: 1,
    hint: '0-1, higher = stricter',
  },
  {
    path: 'semantic.similarityBucketSize',
    label: 'Similarity Bucket Size',
    step: 0.01,
    min: 0.01,
    max: 0.2,
    hint: 'Grouping range for similar scores',
  },
  {
    path: 'semantic.keywordBoostThreshold',
    label: 'Keyword Boost Threshold',
    step: 0.05,
    min: 0,
    max: 1,
    hint: 'Min similarity for keyword boost',
  },
  {
    path: 'semantic.minimumWordLength',
    label: 'Min Word Length',
    step: 1,
    min: 1,
    max: 5,
    hint: 'Min chars for tokenization',
  },
  {
    path: 'semantic.timeoutMs',
    label: 'Timeout (ms)',
    step: 500,
    min: 1000,
    max: 10000,
    hint: 'API timeout in milliseconds',
  },
]

const rrfFields: ConfigField[] = [
  { path: 'rrf.k', label: 'RRF K Constant', step: 10, min: 1, max: 100, hint: 'Rank weighting (60 = standard)' },
  { path: 'rrf.threshold', label: 'RRF Threshold', step: 0.005, min: 0, max: 0.1, hint: 'Min RRF score to include' },
]

const generalFields: ConfigField[] = [
  { path: 'minQueryLength', label: 'Min Query Length', step: 1, min: 1, max: 10, hint: '1-10 characters' },
  { path: 'debounceMs', label: 'Debounce (ms)', step: 50, min: 0, max: 2000, hint: '0-2000ms' },
  {
    path: 'scoreDiffThreshold',
    label: 'Score Diff Threshold',
    step: 0.001,
    min: 0,
    max: 0.1,
    hint: 'Score diff for category ranking',
  },
  { path: 'maxResults', label: 'Max Results', step: 5, min: 5, max: 50, hint: 'Maximum results to return' },
]

const allFields = [...fuzzyFields, ...semanticFields, ...rrfFields, ...generalFields]

export interface SearchConfigFormProps {
  /** Current config values (merged LD + DevTools overrides) */
  config: SmartSearchConfig
  /** Base config from LaunchDarkly (used for comparison indicator and reset) */
  baseConfig: SmartSearchConfig
  /** Called with the full validated config */
  onConfigChange: (config: SmartSearchConfig) => void
  onReset: () => void
}

/** Helper to get a value from nested config using dot-notation path */
const getConfigValue = (config: SmartSearchConfig, path: ConfigFieldPath): number => {
  if (path.startsWith('fuzzy.')) {
    const key = path.replace('fuzzy.', '') as keyof FuzzySearchConfig
    return config.fuzzy[key] as number
  }
  if (path.startsWith('semantic.')) {
    const key = path.replace('semantic.', '') as keyof SemanticSearchConfig
    return config.semantic[key] as number
  }
  if (path.startsWith('rrf.')) {
    const key = path.replace('rrf.', '') as keyof RRFConfig
    return config.rrf[key]
  }
  return config[path as keyof SmartSearchConfig] as number
}

/** Helper to set a value in nested config using dot-notation path */
const setConfigValue = (config: SmartSearchConfig, path: ConfigFieldPath, value: number): SmartSearchConfig => {
  const newConfig = { ...config }
  if (path.startsWith('fuzzy.')) {
    const key = path.replace('fuzzy.', '') as keyof FuzzySearchConfig
    newConfig.fuzzy = { ...config.fuzzy, [key]: value }
  } else if (path.startsWith('semantic.')) {
    const key = path.replace('semantic.', '') as keyof SemanticSearchConfig
    newConfig.semantic = { ...config.semantic, [key]: value }
  } else if (path.startsWith('rrf.')) {
    const key = path.replace('rrf.', '') as keyof RRFConfig
    newConfig.rrf = { ...config.rrf, [key]: value }
  } else {
    ;(newConfig as Record<string, unknown>)[path] = value
  }
  return newConfig
}

export const SearchConfigForm: FunctionComponent<SearchConfigFormProps> = ({
  config,
  baseConfig,
  onConfigChange,
  onReset,
}) => {
  const { colors } = useTheme()
  const [selectedMode, setSelectedMode] = useState<SmartSearchMode>(config.mode)
  const [selectedPhases, setSelectedPhases] = useState<SearchableItemPhase[]>(
    config.enabledPhases ?? ALL_SEARCHABLE_ITEM_PHASES,
  )

  // Store raw string values for editing (allows typing decimals, backspacing, etc.)
  const initInputValues = useMemo(() => {
    const values: Record<string, string> = { mode: config.mode }
    allFields.forEach((field) => {
      values[field.path] = String(getConfigValue(config, field.path))
    })
    return values
  }, [config])

  const [inputValues, setInputValues] = useState<Record<string, string>>(initInputValues)

  // Sync input values when config prop changes (e.g., after reset)
  useEffect(() => {
    const values: Record<string, string> = { mode: config.mode }
    allFields.forEach((field) => {
      values[field.path] = String(getConfigValue(config, field.path))
    })
    setInputValues(values)
    setSelectedMode(config.mode)
    setSelectedPhases(config.enabledPhases ?? ALL_SEARCHABLE_ITEM_PHASES)
  }, [config])

  const handleValueChange = (path: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [path]: value }))
  }

  const handleModeChange = (mode: SmartSearchMode) => {
    setSelectedMode(mode)
    setInputValues((prev) => ({ ...prev, mode }))
  }

  const handlePhaseToggle = (phase: SearchableItemPhase) => {
    setSelectedPhases((prev) => {
      if (prev.includes(phase)) {
        return prev.filter((p) => p !== phase)
      }
      return [...prev, phase]
    })
  }

  const getFieldError = (field: ConfigField): string | null => {
    const rawValue = inputValues[field.path]
    if (rawValue === undefined || rawValue.trim() === '') {
      return 'Required'
    }
    const numValue = parseFloat(rawValue)
    if (isNaN(numValue)) {
      return 'Must be a number'
    }
    if (numValue < field.min || numValue > field.max) {
      return `Must be ${field.min}-${field.max}`
    }
    return null
  }

  const hasValidationErrors = allFields.some((field) => getFieldError(field) !== null) || selectedPhases.length === 0

  const getValidatedConfig = (): SmartSearchConfig => {
    let validated: SmartSearchConfig = { ...config, mode: selectedMode, enabledPhases: selectedPhases }
    for (const field of allFields) {
      const rawValue = inputValues[field.path]
      const numValue = parseFloat(rawValue)
      if (!isNaN(numValue)) {
        const clampedValue = Math.max(field.min, Math.min(field.max, numValue))
        validated = setConfigValue(validated, field.path, clampedValue)
      }
    }
    return validated
  }

  const hasChanges = !isEqual(getValidatedConfig(), config)

  const handleSubmit = () => {
    const validated = getValidatedConfig()
    onConfigChange(validated)
    // Update input values to reflect clamped values
    const values: Record<string, string> = { mode: validated.mode }
    allFields.forEach((field) => {
      values[field.path] = String(getConfigValue(validated, field.path))
    })
    setInputValues(values)
  }

  const handleReset = () => {
    const values: Record<string, string> = { mode: baseConfig.mode }
    allFields.forEach((field) => {
      values[field.path] = String(getConfigValue(baseConfig, field.path))
    })
    setInputValues(values)
    setSelectedMode(baseConfig.mode)
    setSelectedPhases(baseConfig.enabledPhases ?? ALL_SEARCHABLE_ITEM_PHASES)
    onReset()
  }

  const isBaseValue = (path: ConfigFieldPath) => {
    const numValue = parseFloat(inputValues[path])
    return !isNaN(numValue) && numValue === getConfigValue(baseConfig, path)
  }

  const isPhasesBaseValue = () => {
    const basePhases = baseConfig.enabledPhases ?? ALL_SEARCHABLE_ITEM_PHASES
    return isEqual(selectedPhases.slice().sort(), basePhases.slice().sort())
  }

  const renderFieldSection = (title: string, fields: ConfigField[]) => (
    <>
      <SectionHeader size={BodyTextSize.SMALL} text={title} />
      {fields.map((field) => {
        const error = getFieldError(field)
        return (
          <FieldRow key={field.path}>
            <FieldContent>
              <InputRow>
                <FieldLabel
                  size={BodyTextSize.SMALL}
                  text={`${field.label}${isBaseValue(field.path) ? '' : ' *'}`}
                  color={error ? colors.textError : isBaseValue(field.path) ? colors.textPrimary : colors.textActive}
                />
                <InputContainer testID={tID(`SearchConfigForm-input-${field.path.replace('.', '-')}`)}>
                  <BaseInputField
                    inputValue={inputValues[field.path] ?? ''}
                    onInputChange={(value) => handleValueChange(field.path, value)}
                    borderWidth={1}
                    height={32}
                    multiline={false}
                    width='100%'
                  />
                </InputContainer>
              </InputRow>
              <BodyText
                size={BodyTextSize.CAPTION}
                color={error ? colors.textError : colors.textSecondary}
                text={error ?? field.hint}
              />
            </FieldContent>
          </FieldRow>
        )
      })}
    </>
  )

  return (
    <Container testID={tID('SearchConfigForm')}>
      <SectionHeader size={BodyTextSize.SMALL} text='Search Mode' />
      <ModeSelector>
        {Object.values(SmartSearchMode).map((mode) => (
          <BaseButton
            key={mode}
            buttonType={selectedMode === mode ? ButtonType.PRIMARY : ButtonType.SECONDARY}
            size={ButtonSize.SMALL}
            text={mode.charAt(0).toUpperCase() + mode.slice(1)}
            onPress={() => handleModeChange(mode)}
            testID={tID(`SearchConfigForm-mode-${mode}`)}
          />
        ))}
      </ModeSelector>

      <SectionDivider />
      <BodyText
        size={BodyTextSize.SMALL}
        color={colors.textSecondary}
        text={`Enabled Phases${isPhasesBaseValue() ? '' : ' *'}:`}
      />
      {ALL_SEARCHABLE_ITEM_PHASES.map((phase) => (
        <PhaseToggleRow key={phase}>
          <FieldLabel
            size={BodyTextSize.SMALL}
            text={phase}
            color={selectedPhases.includes(phase) ? colors.textPrimary : colors.textSecondary}
          />
          <ToggleSwitch
            isOn={selectedPhases.includes(phase)}
            onPress={() => handlePhaseToggle(phase)}
            testID={tID(`SearchConfigForm-phase-${phase}`)}
          />
        </PhaseToggleRow>
      ))}
      {selectedPhases.length === 0 && (
        <BodyText size={BodyTextSize.CAPTION} color={colors.textError} text='At least one phase must be selected' />
      )}

      <SectionDivider />
      {(selectedMode === SmartSearchMode.FUZZY || selectedMode === SmartSearchMode.HYBRID) &&
        renderFieldSection('Fuzzy Search (Fuse.js)', fuzzyFields)}
      {(selectedMode === SmartSearchMode.SEMANTIC || selectedMode === SmartSearchMode.HYBRID) &&
        renderFieldSection('Semantic Search (API)', semanticFields)}
      {selectedMode === SmartSearchMode.HYBRID && renderFieldSection('RRF Merge (Hybrid)', rrfFields)}
      {renderFieldSection('General', generalFields)}
      <BodyText size={BodyTextSize.SMALL} color={colors.textSecondary} text='* indicates overridden value' />
      {hasChanges && <BodyText size={BodyTextSize.SMALL} color={colors.textWarning} text='You have unsaved changes' />}
      <ButtonContainer>
        <BaseButton
          buttonType={ButtonType.PRIMARY}
          size={ButtonSize.SMALL}
          text='Apply Config'
          onPress={handleSubmit}
          disabled={hasValidationErrors}
          testID={tID('SearchConfigForm-submit')}
        />
        <SecondaryButton
          isSmall
          text='Reset to Defaults'
          onPress={handleReset}
          testID={tID('SearchConfigForm-reset')}
        />
      </ButtonContainer>
    </Container>
  )
}
